import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Divider,
  Box,
  TextField,
  InputAdornment,
  Tooltip,
  IconButton,
  Collapse
} from '@mui/material';
import {
  Search as SearchIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ExpandLess,
  ExpandMore,
  Check as CheckIcon
} from '@mui/icons-material';
import { createNavigationLinks } from '../navigationConfig';
import { 
  getNavigationColor, 
  filterNavLinks, 
  filterChildrenByRole, 
  highlightText, 
  isPathSelected 
} from '../navigationUtils';
import { ALLOWED_PATHS_WHEN_NOT_CLOCKED_IN } from '../constants';

const LayoutDrawer = ({
  mobileOpen,
  handleDrawerToggle,
  currentDrawerWidth,
  isDrawerCollapsed,
  handleDrawerCollapse,
  branding,
  t,
  shouldShowTriage,
  userRoles = [],
  requiresClockIn,
  isClockedIn,
  clockStatusLoading,
  handleNavigation,
  location,
  isMobile,
  isRTL,
  theme,
  isResizing,
  handleMouseDown,
  resizeRef
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [openCollapse, setOpenCollapse] = useState({});

  // Navigation links
  const navLinks = useMemo(() => {
    return createNavigationLinks(t, shouldShowTriage).filter(link => {
      if (!link.roles) return true;
      // Check if any of the user's roles match the link's allowed roles
      return userRoles.some(userRole => link.roles.includes(userRole));
    });
  }, [t, shouldShowTriage, userRoles]);

  // Helper function to check if a parent item should be selected
  const isParentSelected = useCallback((item) => {
    if (!item.children) return false;
    return item.children.some(child => isPathSelected(child.path, location.pathname));
  }, [location.pathname]);

  const handleCollapse = (key) => {
    setOpenCollapse(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Auto-expand parent items when their children are selected
  useEffect(() => {
    const newOpenCollapse = {};
    navLinks.forEach(item => {
      if (item.children && isParentSelected(item)) {
        newOpenCollapse[item.key] = true;
      }
    });
    
    // Only update if there are actual changes
    setOpenCollapse(prev => {
      const hasChanges = Object.keys(newOpenCollapse).some(key => 
        newOpenCollapse[key] && !prev[key]
      );
      if (hasChanges) {
        return { ...prev, ...newOpenCollapse };
      }
      return prev;
    });
  }, [location.pathname, navLinks, isParentSelected]);

  // Auto-expand parent items that have matching children when searching
  useEffect(() => {
    if (searchQuery) {
      const newOpenCollapse = {};
      navLinks.forEach(item => {
        if (item.children && item.key) {
          const childMatches = item.children.some(child => 
            child.text.toLowerCase().includes(searchQuery.toLowerCase())
          );
          if (childMatches) {
            newOpenCollapse[item.key] = true;
          }
        }
      });
      
      // Only update state if there are actual changes
      setOpenCollapse(prev => {
        const hasChanges = Object.keys(newOpenCollapse).some(key => 
          newOpenCollapse[key] && !prev[key]
        );
        if (hasChanges) {
          return { ...prev, ...newOpenCollapse };
        }
        return prev;
      });
    }
  }, [searchQuery, navLinks]);

  // Filtered navigation links
  const filteredNavLinks = useMemo(() => {
    let filtered = filterNavLinks(navLinks, searchQuery).filter(link => {
      if (!link.roles) return true;
      // Check if any of the user's roles match the link's allowed roles
      return userRoles.some(userRole => link.roles.includes(userRole));
    });

    // Filter children based on user roles
    filtered = filtered.map(link => {
      if (link.children) {
        const filteredChildren = filterChildrenByRole(link.children, userRoles);
        return { ...link, children: filteredChildren };
      }
      return link;
    });
    
    // If clock-in is required but user is not clocked in, only show specific links
    if (requiresClockIn && !isClockedIn && !clockStatusLoading) {
      console.log('🚫 Navigation filtered - user not clocked in', {
        requiresClockIn,
        isClockedIn,
        clockStatusLoading,
        allowedPaths: ALLOWED_PATHS_WHEN_NOT_CLOCKED_IN
      });
      filtered = filtered.filter(link => 
        ALLOWED_PATHS_WHEN_NOT_CLOCKED_IN.includes(link.path) || 
        (link.children && link.children.some(child => ALLOWED_PATHS_WHEN_NOT_CLOCKED_IN.includes(child.path)))
      );
    } else if (!requiresClockIn || isClockedIn) {
      console.log('✅ Navigation allowed - user has access', {
        requiresClockIn,
        isClockedIn,
        clockStatusLoading
      });
    }
    
    return filtered;
  }, [navLinks, userRoles, searchQuery, requiresClockIn, isClockedIn, clockStatusLoading]);

  const drawerContent = (
    <div>
      <Toolbar sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: isDrawerCollapsed ? 'center' : 'space-between', 
        p: 1,
        minHeight: '64px !important'
      }}>
        {!isDrawerCollapsed ? (
          <>
            {branding.logoUrl ? (
              <img src={branding.logoUrl} alt="Logo" style={{ maxHeight: '50px', maxWidth: '100%' }} />
            ) : (
              <Typography variant="h6" noWrap component="div" sx={{ color: branding.primaryColor }}>
                QuantumLeap EMR
              </Typography>
            )}
            <Tooltip title="Collapse sidebar">
              <IconButton onClick={handleDrawerCollapse} size="small">
                <ChevronLeftIcon />
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <Tooltip title="Expand sidebar">
            <IconButton onClick={handleDrawerCollapse} size="small">
              <ChevronRightIcon />
            </IconButton>
          </Tooltip>
        )}
      </Toolbar>
      <Divider />
      {!isDrawerCollapsed && (
        <>
          <Box sx={{ p: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search navigation..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoComplete="off"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                }
              }}
            />
          </Box>
          <Divider />
        </>
      )}
      <List sx={{ px: 1 }}>
        {filteredNavLinks.map((item) => {
          const itemColor = getNavigationColor(item.text);
          return (
            <React.Fragment key={item.text}>
              {item.children ? (
                <>
                  <Tooltip title={isDrawerCollapsed ? item.text : ''} placement="right">
                    <ListItemButton 
                      onClick={() => isDrawerCollapsed ? handleNavigation(item.children[0]?.path) : handleCollapse(item.key)}
                      selected={isParentSelected(item)}
                      sx={{ 
                        minHeight: 56,
                        justifyContent: isDrawerCollapsed ? 'center' : 'initial',
                        px: 2,
                        py: 1.5,
                        mb: 1,
                        borderRadius: 2,
                        backgroundColor: itemColor,
                        color: 'white',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          backgroundColor: itemColor,
                          filter: 'brightness(1.1)',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                        },
                        '&.Mui-selected': {
                          backgroundColor: itemColor + ' !important',
                          color: 'black !important',
                          opacity: '1 !important',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1) !important',
                          '& .MuiListItemIcon-root': {
                            color: 'black !important',
                          },
                          '& .MuiListItemText-primary': {
                            color: 'black !important',
                          },
                          '& .MuiSvgIcon-root': {
                            color: 'white !important',
                          },
                          '&:hover': {
                            backgroundColor: itemColor,
                            filter: 'brightness(1.1)',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                            color: 'black !important',
                            '& .MuiListItemIcon-root': {
                              color: 'black !important',
                            },
                            '& .MuiListItemText-primary': {
                              color: 'black !important',
                            },
                            '& .MuiSvgIcon-root': {
                              color: 'white !important',
                            },
                          }
                        }
                      }}
                    >
                      <ListItemIcon sx={{ 
                        minWidth: 0, 
                        mr: isDrawerCollapsed ? 0 : 2, 
                        justifyContent: 'center',
                        color: 'white'
                      }}>
                        {item.icon}
                      </ListItemIcon>
                      {!isDrawerCollapsed && (
                        <>
                          <ListItemText 
                            primary={highlightText(item.text, searchQuery)}
                            sx={{
                              '& .MuiListItemText-primary': {
                                fontWeight: 600,
                                fontSize: '0.95rem'
                              }
                            }}
                          />
                          {isParentSelected(item) && <CheckIcon sx={{ mr: 1, fontSize: '1.1rem' }} />}
                          {openCollapse[item.key] ? <ExpandLess /> : <ExpandMore />}
                        </>
                      )}
                    </ListItemButton>
                  </Tooltip>
                  {!isDrawerCollapsed && (
                    <Collapse in={openCollapse[item.key]} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding sx={{ pl: 2, pr: 1 }}>
                        {item.children.map((child) => (
                          <ListItemButton
                            key={child.text}
                            sx={{
                              pl: 5,
                              pr: 2,
                              py: 1,
                              mb: 0.5,
                              borderRadius: 1.5,
                              backgroundColor: itemColor,
                              color: 'white',
                              opacity: 1,
                              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                              transition: 'all 0.2s ease-in-out',
                              '&:hover': {
                                backgroundColor: itemColor,
                                opacity: 1,
                                filter: 'brightness(1.1)',
                                transform: 'translateX(4px)',
                                boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                              },
                              '&.Mui-selected': {
                                backgroundColor: itemColor + ' !important',
                                color: 'black !important',
                                opacity: '1 !important',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.1) !important',
                                '& .MuiListItemIcon-root': {
                                  color: 'black !important',
                                },
                                '& .MuiListItemText-primary': {
                                  color: 'black !important',
                                },
                                '& .MuiSvgIcon-root': {
                                  color: 'white !important',
                                },
                                '&:hover': {
                                  backgroundColor: itemColor,
                                  opacity: 1,
                                  filter: 'brightness(1.1)',
                                  transform: 'translateX(4px)',
                                  boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                                  color: 'black !important',
                                  '& .MuiListItemIcon-root': {
                                    color: 'black !important',
                                  },
                                  '& .MuiListItemText-primary': {
                                    color: 'black !important',
                                  },
                                  '& .MuiSvgIcon-root': {
                                    color: 'white !important',
                                  },
                                }
                              }
                            }}
                            selected={isPathSelected(child.path, location.pathname)}
                            onClick={() => handleNavigation(child.path)}
                          >
                            <ListItemIcon sx={{ minWidth: 0, mr: 2, color: 'white', opacity: 1 }}>{child.icon}</ListItemIcon>
                            <ListItemText
                              primary={highlightText(child.text, searchQuery)}
                              sx={{ '& .MuiListItemText-primary': { fontWeight: 500, fontSize: '0.875rem' } }}
                            />
                            {isPathSelected(child.path, location.pathname) && <CheckIcon sx={{ fontSize: '1rem' }} />}
                          </ListItemButton>
                        ))}
                      </List>
                    </Collapse>
                  )}
                </>
              ) : (
                <Tooltip title={isDrawerCollapsed ? item.text : ''} placement="right">
                  <ListItemButton 
                    selected={isPathSelected(item.path, location.pathname)} 
                    onClick={() => handleNavigation(item.path)}
                    sx={{ 
                      minHeight: 56,
                      justifyContent: isDrawerCollapsed ? 'center' : 'initial',
                      px: 2,
                      py: 1.5,
                      mb: 1,
                      borderRadius: 2,
                      backgroundColor: itemColor,
                      color: 'white',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        backgroundColor: itemColor,
                        filter: 'brightness(1.1)',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                      },
                      '&.Mui-selected': {
                        backgroundColor: itemColor + ' !important',
                        color: 'black !important',
                        opacity: '1 !important',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1) !important',
                        '& .MuiListItemIcon-root': {
                          color: 'black !important',
                        },
                        '& .MuiListItemText-primary': {
                          color: 'black !important',
                        },
                        '& .MuiSvgIcon-root': {
                          color: 'white !important',
                        },
                        '&:hover': {
                          backgroundColor: itemColor,
                          filter: 'brightness(1.1)',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                          color: 'black !important',
                          '& .MuiListItemIcon-root': {
                            color: 'black !important',
                          },
                          '& .MuiListItemText-primary': {
                            color: 'black !important',
                          },
                          '& .MuiSvgIcon-root': {
                            color: 'white !important',
                          },
                        }
                      }
                    }}
                  >
                    <ListItemIcon sx={{ 
                      minWidth: 0, 
                      mr: isDrawerCollapsed ? 0 : 2, 
                      justifyContent: 'center',
                      color: 'white'
                    }}>
                      {item.icon}
                    </ListItemIcon>
                    {!isDrawerCollapsed && (
                      <>
                        <ListItemText 
                          primary={highlightText(item.text, searchQuery)}
                          sx={{
                            '& .MuiListItemText-primary': {
                              fontWeight: 600,
                              fontSize: '0.95rem'
                            }
                          }}
                        />
                        {isPathSelected(item.path, location.pathname) && <CheckIcon sx={{ fontSize: '1.1rem' }} />}
                      </>
                    )}
                  </ListItemButton>
                </Tooltip>
              )}
            </React.Fragment>
          );
        })}
      </List>
    </div>
  );

  return (
    <Drawer
      variant={isMobile ? "temporary" : "permanent"}
      open={isMobile ? mobileOpen : true}
      onClose={handleDrawerToggle}
      ModalProps={{
        keepMounted: true, // Better open performance on mobile.
      }}
      sx={{
        display: { xs: 'block', md: 'block' },
        width: currentDrawerWidth,
        flexShrink: 0,
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
        '& .MuiDrawer-paper': { 
          boxSizing: 'border-box', 
          width: currentDrawerWidth,
          left: isRTL ? 'auto' : 0,
          right: isRTL ? 0 : 'auto',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          overflowX: 'hidden',
        },
      }}
    >
      {drawerContent}
      
      {/* Resize handle for desktop non-collapsed state */}
      {!isMobile && !isDrawerCollapsed && (
        <Box
          ref={resizeRef}
          onMouseDown={handleMouseDown}
          sx={{
            position: 'absolute',
            top: 0,
            ...(isRTL ? { left: 0 } : { right: 0 }),
            width: '4px',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.12)',
            cursor: isRTL ? 'col-resize' : 'col-resize',
            zIndex: 1000,
            borderRadius: isRTL ? '3px 0 0 3px' : '0 3px 3px 0',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: theme.palette.primary.main,
              opacity: 0.8,
              width: '6px',
              ...(isRTL ? { left: -1 } : { right: -1 }),
            },
            ...(isResizing && {
              backgroundColor: theme.palette.primary.main,
              opacity: 1,
              width: '6px',
              ...(isRTL ? { left: -1 } : { right: -1 }),
            }),
          }}
        >
          {/* Visual indicator dots for better UX */}
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '2px',
              height: '20px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-around',
              '&::before, &::after': {
                content: '""',
                width: '2px',
                height: '2px',
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                borderRadius: '50%',
              },
              '&::before': {
                marginBottom: '2px',
              },
              '&::after': {
                marginTop: '2px',
              },
            }}
          />
        </Box>
      )}
    </Drawer>
  );
};

export default LayoutDrawer; 